import React from "react";
import {
  Box,
  MainButton,
  MainText,
  MainTitle,
  MySafeAreaView,
} from "app/components/common";
import i18n from "@microdoing/shared/i18n";
import moment from "moment";
import analytics from "@react-native-firebase/analytics";
import { StyleSheet, ScrollView, Platform, Linking } from "react-native";
import {
  cancelAllNotifications,
  scheduleGoalNotif,
} from "@microdoing/shared/services/notifService";
import {
  updateUserWithoutResponseRequest,
  modifyUser,
} from "@microdoing/shared/services/user/actions";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import hasNotificationPermission from "@microdoing/shared/utils/hasNotificationPermission";
import { signupOnboardingEndRequest } from "@microdoing/shared/services/signup/actions";
import requestNotificationPermission from "@microdoing/shared/utils/requestNotificationPermission";
import AllowNotificationCard from "./AllowNotificationCard";
import JouvieAllowNotification from "./JouvieAllowNotification";
import { CommonActions } from "@react-navigation/native";
import { showAlert } from "@microdoing/shared/services/profileSettings/actions";
import { getSignupRequesting } from "@microdoing/shared/services/signup/selectors";
import HeaderScreen from "app/components/common/HeaderScreen";
import theme from "app/Theme/Theme";
import { useDispatch, useSelector } from "react-redux";

const styles = StyleSheet.create({
  scrollView: {
    flexGrow: 1,
    justifyContent: "space-between",
  },
});

const AllowNotificationsCardScreen = ({ navigation, route }) => {
  const isComingFromSignup = route?.params?.isComingFromSignup;
  const isComingFromFirstAction = route?.params?.isComingFromFirstAction;
  const requesting = useSelector(getSignupRequesting);

  const userId = useSelector(getUserId);

  const dispatch = useDispatch();

  const handlePressNext = async () => {
    await cancelAllNotifications();
    await scheduleGoalNotif();
    if (isComingFromSignup) {
      return dispatch(signupOnboardingEndRequest());
    } else if (isComingFromFirstAction) {
      dispatch(
        showAlert({
          title: i18n.t("modify-profile-success-title"),
          message: i18n.t("modify-profile-success-message"),
        }),
      );
      return navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: "Main" }],
        }),
      );
    } else {
      return navigation.goBack();
    }
  };

  const handlePressAuthorize = async () => {
    try {
      if (Platform.OS !== "web") {
        analytics().logEvent("press_authorize_notifications", {
          screen: "allow-notification-card-screen",
        });
      }

      const accepted = await requestNotificationPermission();
    } finally {
      const enabled = await hasNotificationPermission();

      if (enabled) {
        dispatch(
          updateUserWithoutResponseRequest({
            userId,
            hasNotificationsEnabled: true,
            hasNotificationsEnabledCheckDate: moment().toDate(),
          }),
        );
        dispatch(modifyUser({ hasNotificationsEnabled: true }));

        if (Platform.OS !== "web") {
          analytics().logEvent("press_accept_notifications", {
            screen: "allow-notification-card-screen",
          });
        }
      } else {
        if (Platform.OS !== "web") {
          analytics().logEvent("press_reject_notifications", {
            screen: "allow-notification-card-screen",
          });
        }
        if (Platform.OS === "ios") {
          Linking.canOpenURL("app-settings:")
            .then(supported => {
              if (!supported) {
                console.log("Can't handle settings url");
              } else {
                return Linking.openURL("app-settings:");
              }
            })
            .catch(err => console.error("An error occurred", err));
        } else {
          Linking.openSettings();
        }
      }
    }

    handlePressNext();
  };

  const handlePressReject = () => {
    if (Platform.OS !== "web") {
      analytics().logEvent("press_not_authorize_notifications", {
        screen: "allow-notification-card-screen",
      });
    }

    dispatch(
      updateUserWithoutResponseRequest({
        userId,
        hasNotificationsEnabled: false,
        hasNotificationsEnabledCheckDate: moment().toDate(),
      }),
    );
    handlePressNext();
  };

  return (
    <MySafeAreaView flex={1} backgroundColor="neutral.white">
      <ScrollView contentContainerStyle={styles.scrollView}>
        <Box variant="screenView" flex={1}>
          <Box
            flexDirection="row"
            width="100%"
            alignSelf="center"
            alignItems="center"
            marginBottom="sm"
          >
            <Box
              width="100%"
              paddingTop="sm"
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <MainTitle variant="l8-bold" textAlign="left">
                {i18n.t("activate-notifications")}
              </MainTitle>
              <MainText variant="l3-semibold" textAlign="left" marginTop="xxs">
                {i18n.t("it-is-important")}
              </MainText>
            </Box>
          </Box>
          <Box alignItems="center" flex={1}>
            <AllowNotificationCard
              onPressAuthorize={handlePressAuthorize}
              onPressReject={handlePressReject}
            />
          </Box>
        </Box>
        <Box>
          <JouvieAllowNotification />
          <Box variant="screenView">
            <MainButton
              isDisabled={!requesting}
              onPress={handlePressNext}
              text={i18n.t("done")}
              isLoading={requesting}
              textColor={!requesting ? "neutral.700" : "neutral.white"}
            />
          </Box>
        </Box>
      </ScrollView>
    </MySafeAreaView>
  );
};

export default AllowNotificationsCardScreen;
